import React, { useId, useState } from 'react'
import { Checkbox, Icon } from 'semantic-ui-react'

import DocPreview from 'components/DocPreview'

import type { ArtboardState, IdType } from 'EditorApp/types'

const THUMBNAIL_WIDTH = 44
const THUMBNAIL_HEIGHT = 28

// Artboard id to the selected page numbers within it, counted from the start of the artboard, as
// the export URL names them.
export type PageSelection = Record<IdType, number[]>

type SelectionState = 'all' | 'some' | 'none'

export const artboardPageNumbers = (artboard: ArtboardState): number[] =>
  artboard.pageOrder.map((_, index) => index + 1)

export const everyPage = (artboards: ArtboardState[]): PageSelection =>
  Object.fromEntries(
    artboards.map((artboard) => [artboard.id, artboardPageNumbers(artboard)])
  )

export const countPages = (selection: PageSelection): number =>
  Object.values(selection).reduce((count, pages) => count + pages.length, 0)

const selectionState = (
  selectedCount: number,
  pageCount: number
): SelectionState => {
  if (selectedCount === 0) {
    return 'none'
  }

  return selectedCount === pageCount ? 'all' : 'some'
}

// DocPreview renders the first page of the markup it is given, so each thumbnail gets a single-page
// copy of its artboard.
const singlePageMarkup = (
  artboard: ArtboardState,
  pageId: IdType
): ArtboardState => ({
  ...artboard,
  pageOrder: [pageId],
  pages: { [pageId]: artboard.pages[pageId] },
})

interface SelectionCheckboxProps {
  id: string
  label: string
  state: SelectionState
  onChange: (selected: boolean) => void
}

const SelectionCheckbox = ({
  id,
  label,
  state,
  onChange,
}: SelectionCheckboxProps) => (
  <Checkbox
    id={id}
    label={label}
    checked={state === 'all'}
    indeterminate={state === 'some'}
    onChange={() => onChange(state !== 'all')}
  />
)

interface PageRowProps {
  artboard: ArtboardState
  pageId: IdType
  pageNumber: number
  checkboxId: string
  selected: boolean
  fontFamily?: string
  onChange: (selected: boolean) => void
}

const PageRow = ({
  artboard,
  pageId,
  pageNumber,
  checkboxId,
  selected,
  fontFamily,
  onChange,
}: PageRowProps) => {
  const scale = Math.min(
    THUMBNAIL_WIDTH / artboard.pageWidth,
    THUMBNAIL_HEIGHT / artboard.pageHeight
  )

  return (
    <li className="ExportMenu__tree-page">
      <label
        htmlFor={checkboxId}
        className={`ExportMenu__tree-thumbnail${
          selected ? ' ExportMenu__tree-thumbnail--selected' : ''
        }`}
        style={{ width: THUMBNAIL_WIDTH, height: THUMBNAIL_HEIGHT }}
      >
        <div
          className="ExportMenu__tree-thumbnail-canvas"
          style={{
            width: artboard.pageWidth * scale,
            height: artboard.pageHeight * scale,
          }}
        >
          <div
            className="ExportMenu__tree-thumbnail-content"
            style={{ transform: `scale(${scale})` }}
          >
            <DocPreview
              fontFamily={fontFamily}
              markup={singlePageMarkup(artboard, pageId)}
              zoom={scale}
              previewImage={true}
            />
          </div>
        </div>
      </label>
      <Checkbox
        id={checkboxId}
        label={`page ${pageNumber}`}
        checked={selected}
        onChange={(_, data) => onChange(!!data.checked)}
      />
    </li>
  )
}

interface Props {
  artboards: ArtboardState[]
  selection: PageSelection
  onChange: (selection: PageSelection) => void
  fontFamily?: string
}

const ExportPageTree = ({
  artboards,
  selection,
  onChange,
  fontFamily,
}: Props) => {
  const idPrefix = useId()
  const [collapsedArtboardIds, setCollapsedArtboardIds] = useState<string[]>([])

  const selectedIn = (artboard: ArtboardState) => selection[artboard.id] || []

  const pageCount = countPages(everyPage(artboards))

  const setPages = (artboard: ArtboardState, pageNumbers: number[]) =>
    onChange({
      ...selection,
      [artboard.id]: [...pageNumbers].sort((a, b) => a - b),
    })

  const setPage = (
    artboard: ArtboardState,
    pageNumber: number,
    selected: boolean
  ) => {
    const otherPages = selectedIn(artboard).filter(
      (selectedNumber) => selectedNumber !== pageNumber
    )

    setPages(artboard, selected ? [...otherPages, pageNumber] : otherPages)
  }

  const setAll = (selected: boolean) =>
    onChange(
      selected
        ? everyPage(artboards)
        : Object.fromEntries(artboards.map((artboard) => [artboard.id, []]))
    )

  const toggleCollapsed = (artboardId: string) => {
    setCollapsedArtboardIds((ids) =>
      ids.includes(artboardId)
        ? ids.filter((id) => id !== artboardId)
        : [...ids, artboardId]
    )
  }

  const pageRows = (artboard: ArtboardState) =>
    artboard.pageOrder.map((pageId, index) => {
      const pageNumber = index + 1

      return (
        <PageRow
          key={pageId}
          artboard={artboard}
          pageId={pageId}
          pageNumber={pageNumber}
          checkboxId={`${idPrefix}-${artboard.id}-page-${pageNumber}`}
          selected={selectedIn(artboard).includes(pageNumber)}
          fontFamily={fontFamily}
          onChange={(selected) => setPage(artboard, pageNumber, selected)}
        />
      )
    })

  return (
    <div className="ExportMenu__tree">
      {pageCount > 1 && (
        <div className="ExportMenu__tree-select-all">
          <SelectionCheckbox
            id={`${idPrefix}-all`}
            label="select all"
            state={selectionState(countPages(selection), pageCount)}
            onChange={setAll}
          />
        </div>
      )}

      {artboards.length === 1 ? (
        <ul className="ExportMenu__tree-pages">{pageRows(artboards[0])}</ul>
      ) : (
        <ul className="ExportMenu__tree-artboards">
          {artboards.map((artboard) => {
            const collapsed = collapsedArtboardIds.includes(artboard.id)

            return (
              <li key={artboard.id} className="ExportMenu__tree-artboard">
                <div className="ExportMenu__tree-artboard-row">
                  <button
                    type="button"
                    className="ExportMenu__tree-toggle"
                    aria-expanded={!collapsed}
                    aria-label={`${collapsed ? 'show' : 'hide'} ${
                      artboard.name
                    } pages`}
                    onClick={() => toggleCollapsed(artboard.id)}
                  >
                    <Icon name={collapsed ? 'caret right' : 'caret down'} />
                  </button>
                  <SelectionCheckbox
                    id={`${idPrefix}-artboard-${artboard.id}`}
                    label={artboard.name}
                    state={selectionState(
                      selectedIn(artboard).length,
                      artboard.pageOrder.length
                    )}
                    onChange={(selected) =>
                      setPages(
                        artboard,
                        selected ? artboardPageNumbers(artboard) : []
                      )
                    }
                  />
                  <span className="ExportMenu__tree-dimensions">
                    {artboard.pageWidth}×{artboard.pageHeight}
                  </span>
                </div>

                {!collapsed && (
                  <ul className="ExportMenu__tree-pages">
                    {pageRows(artboard)}
                  </ul>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default ExportPageTree
